import { CheckCircle2, Info, Play, Plus, RefreshCw, RotateCcw, Square, XCircle } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { ConfirmDialog } from '@/components/ConfirmDialog'
import { StepRail, type Step } from '@/components/data/StepRail'
import { SectionBoundary } from '@/components/feedback/SectionBoundary'
import { Page } from '@/components/layout/Page'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import {
  ApiError,
  confirmApply,
  confirmRevert,
  countIssues,
  createRun,
  getRun,
  getRuns,
  isActive,
  newPairDraft,
  plannedWrites,
  previewApply,
  previewRevert,
  resumeRun,
  retryRun,
  stopRun,
  type ApplyPreview,
  type PairDraft,
  type RevertPreview,
  type RunDetail,
  type RunStatus,
  type RunSummary,
} from '@/lib/fieldCopy'
import { FieldCopyHistory } from '@/pages/FieldCopy/FieldCopyHistory'
import { PAIR_GRID, PairEditor } from '@/pages/FieldCopy/PairEditor'
import { RunReport } from '@/pages/FieldCopy/RunReport'
import { Caption } from '@/pages/FieldCopy/shared'
import { BlockTitle } from '@/pages/Tge/TgeReports'

const POLL_INTERVAL_MS = 3000
const COUNT_DEBOUNCE_MS = 600
const MAX_PAIRS = 10
const SETTLED: RunStatus[] = ['applied', 'paused', 'cancelled', 'failed']

type Notice = { kind: 'info' | 'success' | 'error'; text: string }
type Confirm = { kind: 'apply'; preview: ApplyPreview } | { kind: 'revert'; preview: RevertPreview }

const NOTICE_STYLE = {
  info: { icon: Info, tone: 'text-text-2' },
  success: { icon: CheckCircle2, tone: 'text-ok' },
  error: { icon: XCircle, tone: 'text-danger' },
} as const

function errText(err: unknown): string {
  return err instanceof ApiError ? `${err.detail} (HTTP ${err.status})` : (err as Error).message
}

function applyBody(p: ApplyPreview): string {
  const rules = p.automation_rules.length
    ? ` ${p.automation_rules.length} Automation rule(s) react to the target field.`
    : ''
  return `Run #${p.run_id}: Write ${p.rows} values into ${p.issues} issues — ${p.bulk_rows} via bulk, ${p.single_rows} one by one. Notifications are off.${rules}`
}

function revertBody(p: RevertPreview): string {
  return `Run #${p.run_id}: Restore the previous value in ${p.rows} rows across ${p.issues} issues. Rows changed since the copy are left alone as drifted.`
}

/** Plan → Apply → Revert, by the status of the open run. */
function stepsOf(run: RunDetail | null): Step[] {
  const s = run?.status
  const st = (plan: Step['state'], apply: Step['state'], revert: Step['state']): Step[] => [
    { label: 'Plan', state: plan }, { label: 'Apply', state: apply }, { label: 'Revert', state: revert },
  ]
  // a plan that failed or was stopped before it was built is still the current step
  const unplanned = (s === 'failed' || s === 'cancelled') && !run?.counters?.last_phase
  if (!run || s === 'planning' || unplanned) return st('current', 'todo', 'todo')
  if (s === 'applied') return st('done', 'done', 'todo')
  if (s === 'reverting') return st('done', 'done', 'current')
  if (s === 'reverted') return st('done', 'done', 'done')
  return st('done', 'current', 'todo')
}

export default function FieldCopyPage() {
  const [jql, setJql] = useState('')
  const [count, setCount] = useState<{ count: number; max: number } | null>(null)
  const [countError, setCountError] = useState<string | null>(null)
  const [pairs, setPairs] = useState<PairDraft[]>(() => [newPairDraft()])
  const [run, setRun] = useState<RunDetail | null>(null)
  const [runs, setRuns] = useState<RunSummary[]>([])
  const [view, setView] = useState<'setup' | 'report'>('setup')
  const [notice, setNotice] = useState<Notice | null>(null)
  const [busy, setBusy] = useState(false)
  const [confirm, setConfirm] = useState<Confirm | null>(null)
  const pollRef = useRef<number | null>(null)
  // The run being polled: a late getRun answer for a previously watched run is dropped,
  // so it can neither replace the report nor stop polling of the current run.
  const watchedRef = useRef<number | null>(null)

  const refreshRuns = useCallback(() => {
    getRuns().then(setRuns).catch(() => {})
  }, [])

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      window.clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [])

  const watch = useCallback(
    (runId: number) => {
      stopPolling()
      watchedRef.current = runId
      setView('report')
      const tick = () => {
        getRun(runId)
          .then((detail) => {
            if (watchedRef.current !== runId || detail.id !== runId) return
            setRun(detail)
            if (!isActive(detail.status)) {
              stopPolling()
              refreshRuns()
            }
          })
          .catch(() => {
            // transient fetch failure — keep polling
          })
      }
      tick()
      pollRef.current = window.setInterval(tick, POLL_INTERVAL_MS)
    },
    [refreshRuns, stopPolling],
  )

  useEffect(() => {
    refreshRuns()
    return stopPolling
  }, [refreshRuns, stopPolling])

  // Live approximate count while the JQL is typed.
  useEffect(() => {
    const text = jql.trim()
    if (!text) {
      setCount(null)
      setCountError(null)
      return
    }
    const timer = window.setTimeout(() => {
      countIssues(text)
        .then((c) => {
          setCount(c)
          setCountError(null)
        })
        .catch((err: unknown) => {
          setCount(null)
          setCountError(errText(err))
        })
    }, COUNT_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [jql])

  const updatePair = useCallback((key: number, patch: Partial<PairDraft>) => {
    setPairs((ps) =>
      ps.map((p) => {
        if (p.key !== key) return p
        const next = { ...p, ...patch }
        // append is offered only for multi-value targets
        if (next.mode === 'append' && next.info && !next.info.append_allowed) next.mode = 'only_empty'
        return next
      }),
    )
  }, [])

  const removePair = useCallback((key: number) => {
    setPairs((ps) => ps.filter((p) => p.key !== key))
  }, [])

  const running = run !== null && isActive(run.status)
  const pairsReady = pairs.every((p) => Boolean(p.info?.converter) && !p.checking)
  const overLimit = count !== null && count.count > count.max
  const planHint = !jql.trim()
    ? 'Enter a JQL query.'
    : !pairsReady
      ? 'Every pair needs a converter — fix or remove the pairs marked red.'
      : overLimit
        ? `The query matches more than ${count?.max} issues (FIELD_COPY_MAX_ISSUES).`
        : running
          ? 'Another run is in progress.'
          : null
  const canPlan = planHint === null && !busy

  // the report boundary resets when the run's content changes, not on every 3 s poll
  // (each poll returns a new object — keying on it made a broken report flicker)
  const reportKey = run
    ? `${run.id}|${run.status}|${run.finished_at ?? ''}|${JSON.stringify(run.by_status)}|${JSON.stringify(run.counters?.last_phase ?? null)}`
    : null

  // actions available on the open run
  const reportRun = view === 'report' ? run : null
  const n = (k: string) => reportRun?.by_status[k] ?? 0
  const lastPhase = reportRun?.counters?.last_phase
  const settled = !!reportRun && SETTLED.includes(reportRun.status)
  const canApply = reportRun?.status === 'planned' && plannedWrites(reportRun.counters) > 0
  const canResume = !!reportRun && (['paused', 'cancelled', 'failed'] as RunStatus[]).includes(reportRun.status) && !!lastPhase
  const canRetry = settled && lastPhase === 'apply' && n('failed') + n('uncertain') > 0
  // failed or stopped before the plan was built: nothing to resume, the plan starts over
  const canReplan = !!reportRun && (['failed', 'cancelled'] as RunStatus[]).includes(reportRun.status) && !lastPhase
  const canRevert = settled && n('applied') + n('uncertain') > 0

  async function startPlan(runJql: string, runPairs: { source: string; target: string; mode: PairDraft['mode'] }[], from?: number) {
    setBusy(true)
    setNotice(null)
    try {
      const { run_id } = await createRun(runJql, runPairs)
      const origin = from === undefined ? '' : ` with the JQL and pairs of run #${from}`
      setNotice({ kind: 'info', text: `Run #${run_id}: building the plan${origin} — nothing is written yet.` })
      refreshRuns()
      watch(run_id)
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setNotice({ kind: 'info', text: 'A field copy run is already in progress — open it from the run history.' })
        refreshRuns()
      } else {
        setNotice({ kind: 'error', text: `Plan failed: ${errText(err)}` })
      }
    } finally {
      setBusy(false)
    }
  }

  const onPlan = () =>
    startPlan(
      jql.trim(),
      pairs.map(({ source, target, mode }) => ({ source: source.trim(), target: target.trim(), mode })),
    )

  /** The plan broke (a dropped connection) or was stopped: same JQL and pairs, new run. */
  const onReplan = (r: RunDetail) =>
    startPlan(r.jql, r.pairs.map(({ source, target, mode }) => ({ source, target, mode })), r.id)

  async function act(runId: number, label: string, fn: () => Promise<unknown>) {
    setBusy(true)
    try {
      await fn()
      setNotice({ kind: 'info', text: `Run #${runId}: ${label}.` })
      watch(runId)
    } catch (err) {
      setNotice({ kind: 'error', text: `${label} failed: ${errText(err)}` })
    } finally {
      setBusy(false)
    }
  }

  async function openConfirm(kind: 'apply' | 'revert') {
    if (!run) return
    setBusy(true)
    try {
      if (kind === 'apply') setConfirm({ kind, preview: await previewApply(run.id) })
      else setConfirm({ kind, preview: await previewRevert(run.id) })
    } catch (err) {
      setNotice({ kind: 'error', text: errText(err) })
    } finally {
      setBusy(false)
    }
  }

  async function onConfirm() {
    if (!confirm) return
    // the run whose preview the dialog shows — the report may have switched meanwhile
    const runId = confirm.preview.run_id
    const kind = confirm.kind
    await act(runId, kind === 'apply' ? 'writing started' : 'revert started', () =>
      kind === 'apply' ? confirmApply(runId) : confirmRevert(runId),
    )
    setConfirm(null)
  }

  const noticeStyle = notice ? NOTICE_STYLE[notice.kind] : null
  const NoticeIcon = noticeStyle?.icon ?? Info

  return (
    <Page
      title="Field copy"
      hint="Copy values from one Jira field into another across a JQL selection. The plan is read-only; apply writes with the service account — bulk for repeated values, one PUT per issue otherwise, notifications off — and can be reverted."
      context={(
        <>
          <StepRail steps={stepsOf(reportRun)} />
          {reportRun
            ? (
              <span className="ml-2 inline-flex min-w-0 items-center gap-1.5 rounded-md border border-input bg-surface px-2.5 py-1 text-[12px] text-text-2">
                <span className="truncate font-mono text-foreground" title={reportRun.jql}>{reportRun.jql}</span>
                <span className="shrink-0">· {reportRun.pairs.length === 1 ? '1 pair' : `${reportRun.pairs.length} pairs`}</span>
              </span>
            )
            : <span className="ml-2 text-[12px] text-text-2">New run</span>}
        </>
      )}
      actions={view === 'report' && reportRun
        ? (
          <>
            {isActive(reportRun.status) && (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => act(reportRun.id, 'stop requested', () => stopRun(reportRun.id))}>
                <Square aria-hidden />
                Stop
              </Button>
            )}
            {canReplan && (
              <Button size="sm" disabled={busy} onClick={() => void onReplan(reportRun)}>
                <RefreshCw aria-hidden />
                Plan again
              </Button>
            )}
            {canResume && (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => act(reportRun.id, 'resumed', () => resumeRun(reportRun.id))}>
                <Play aria-hidden />
                Resume
              </Button>
            )}
            {canRetry && (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => act(reportRun.id, 'retry started', () => retryRun(reportRun.id))}>
                <RefreshCw aria-hidden />
                Retry failed
              </Button>
            )}
            {canApply && (
              <Button size="sm" disabled={busy} onClick={() => void openConfirm('apply')}>
                <Play aria-hidden />
                Apply plan
              </Button>
            )}
            <Button
              size="sm"
              variant="destructive"
              disabled={busy || !canRevert}
              title={canRevert ? undefined : 'Revert is available once the run has applied changes'}
              onClick={() => void openConfirm('revert')}
            >
              <RotateCcw aria-hidden />
              Revert
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setNotice(null); setView('setup') }}>
              <Plus aria-hidden />
              New run
            </Button>
          </>
        )
        : run && (
          <Button size="sm" variant="outline" onClick={() => setView('report')}>
            Back to run #{run.id}
          </Button>
        )}
    >
      <div className="flex min-h-0 flex-1">
        <div className="flex min-w-0 flex-1 flex-col">
          {notice && noticeStyle && (
            <div className="shrink-0 border-b border-line px-4 py-1.5 text-[12px]">
              <span role="status" className={`flex items-center gap-1.5 ${noticeStyle.tone}`}>
                <NoticeIcon className="size-3.5 shrink-0" aria-hidden />
                {notice.text}
              </span>
            </div>
          )}

          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            {view === 'setup' && (
              <div className="space-y-5 px-4 py-3">
                <section>
                  <label htmlFor="fc-jql"><Caption>JQL</Caption></label>
                  <Textarea
                    id="fc-jql"
                    value={jql}
                    onChange={(e) => setJql(e.target.value)}
                    placeholder={'project = DOM AND "Domain name (migrated)" is not EMPTY'}
                    className="mt-1.5 min-h-20 font-mono text-[13px]"
                  />
                  <p className={`mt-1.5 font-mono text-[11.5px] ${overLimit || countError ? 'text-danger' : 'text-text-3'}`}>
                    {countError ??
                      (count
                        ? `≈ ${count.count.toLocaleString()} issues${overLimit ? ` — over the limit of ${count.max.toLocaleString()}` : ''}`
                        : 'the approximate count appears as you type')}
                  </p>
                </section>

                <section>
                  <BlockTitle>Field pairs</BlockTitle>
                  <div className={`${PAIR_GRID} border-b border-line pb-1.5`}>
                    <span className="text-[11px] font-medium uppercase tracking-[0.05em] text-text-3">#</span>
                    <span className="text-[11px] font-medium uppercase tracking-[0.05em] text-text-3">Source field</span>
                    <span />
                    <span className="text-[11px] font-medium uppercase tracking-[0.05em] text-text-3">Target field</span>
                    <span className="text-[11px] font-medium uppercase tracking-[0.05em] text-text-3">If the target is filled</span>
                    <span />
                  </div>
                  {pairs.map((p, i) => (
                    <PairEditor
                      key={p.key}
                      index={i}
                      pair={p}
                      disabled={busy || running}
                      canRemove={pairs.length > 1}
                      onChange={updatePair}
                      onRemove={removePair}
                    />
                  ))}
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setPairs((ps) => [...ps, newPairDraft()])}
                      disabled={busy || running || pairs.length >= MAX_PAIRS}
                    >
                      <Plus aria-hidden />
                      Add pair
                    </Button>
                    <Button type="button" size="sm" onClick={onPlan} disabled={!canPlan}>
                      Build plan
                    </Button>
                    {planHint && <span className="text-[12px] text-text-2">{planHint}</span>}
                  </div>
                </section>
              </div>
            )}

            {view === 'report' && (
              <SectionBoundary label="Run report" resetKeys={[reportKey]}>
                {run
                  ? <RunReport key={run.id} run={run} />
                  : <p className="px-4 py-3 text-[13px] text-text-2">Loading the run…</p>}
              </SectionBoundary>
            )}
          </div>
        </div>

        <aside aria-label="Run history" className="w-[300px] shrink-0 overflow-y-auto border-l border-line bg-pane px-3 py-3">
          <BlockTitle>Runs</BlockTitle>
          <SectionBoundary label="Run history" onRetry={refreshRuns} resetKeys={[runs]}>
            <FieldCopyHistory
              runs={runs}
              activeId={view === 'report' ? run?.id ?? null : null}
              onOpen={(id) => {
                setNotice(null)
                watch(id)
              }}
            />
          </SectionBoundary>
        </aside>
      </div>

      {confirm && (
        <ConfirmDialog
          title={`${confirm.kind === 'apply' ? 'Apply plan' : 'Revert run'} #${confirm.preview.run_id}`}
          body={confirm.kind === 'apply' ? applyBody(confirm.preview) : revertBody(confirm.preview)}
          confirmLabel={confirm.kind === 'apply' ? 'Apply' : 'Revert'}
          destructive={confirm.kind === 'revert'}
          busy={busy}
          onConfirm={onConfirm}
          onClose={() => setConfirm(null)}
        />
      )}
    </Page>
  )
}
